import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiMinus } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const faqs = [
  {
    question: 'Do I need to change my phone system?',
    answer: 'No. You keep your current number and phone system. Your team still answers first. We add a conditional forwarding rule so unanswered calls route to the AI.',
  },
  {
    question: 'How does the forwarding setup work?',
    answer: 'Your office manager, phone admin, or phone-system vendor adds a "forward if unanswered" rule pointing to the Valtivo AI number. We guide the setup and handle the AI side entirely.',
  },
  {
    question: 'What happens after the AI takes a call?',
    answer: 'The AI captures who called, what they need, and relevant details — then sends a structured summary to your team via email, SMS, Slack, or your CRM. Full recordings and transcripts are available.',
  },
  {
    question: "What if the AI can't answer something?",
    answer: "It captures the issue in full and routes it to your team immediately with a complete summary. No caller is left without a resolution path.",
  },
  {
    question: 'What does setup look like?',
    answer: 'We scope the workflow on a discovery call, then configure the AI agent, integrations, routing logic, and handoff templates. You review and approve before any live calls are routed.',
  },
  {
    question: 'How long does deployment take?',
    answer: 'Standard implementation takes 5–10 business days from signed agreement to go-live. Complex or multi-department setups may take longer.',
  },
  {
    question: 'What industries are the best fit?',
    answer: 'Home services (HVAC, plumbing, electrical, roofing, restoration), auto dealerships (service departments, sales inquiries), and real estate teams. Any business with real inbound call volume and clear cost-per-missed-call is a strong candidate.',
  },
  {
    question: 'What are the honest limitations?',
    answer: 'Valtivo is strong at intake, qualification, FAQ resolution, and structured handoffs. It escalates legal or compliance questions, safety-sensitive situations, and anything requiring human judgment. It does not log into or modify your systems unless explicitly built to do so.',
  },
  {
    question: 'Can this work after hours only?',
    answer: 'Yes. We can configure Valtivo to handle calls only outside your business hours, leaving all in-hours calls to your team. This is a common starting configuration.',
  },
  {
    question: 'Can it transfer callers to my team?',
    answer: 'Yes. When escalation is triggered, the AI can warm-transfer the caller directly to a team member — with full context delivered before they pick up. No re-explaining required.',
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq-section" className="py-20 border-t" style={{ background: '#0a0a0a', borderColor: '#1a1a1a' }}>
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#4ade80' }}>FAQ</p>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#f1f1f1' }}>Common questions.</h2>
        </motion.div>
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className="rounded-xl border overflow-hidden" style={{ borderColor: '#1f1f1f' }}>
              <button onClick={() => toggle(i)}
                className="w-full px-6 py-4 text-left flex items-center justify-between transition-colors"
                style={{ background: openIndex === i ? '#111' : '#0d0d0d' }}>
                <span className="text-sm font-semibold pr-4" style={{ color: '#f1f1f1' }}>{faq.question}</span>
                <SafeIcon icon={openIndex === i ? FiMinus : FiPlus} className="w-4 h-4 flex-shrink-0" style={{ color: '#4ade80' }} />
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
                    <div className="px-6 pb-5 pt-2 text-sm leading-relaxed border-t" style={{ background: '#111', borderColor: '#1f1f1f', color: '#888' }}>
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;